/**
 * Display metadata for the course selector: blurbs, difficulty, theme swatches,
 * and scroll/chapter helpers. Pure data — no Canvas, no DOM.
 */

export type CourseDifficulty = 'EASY' | 'MED' | 'HARD' | 'CUSTOM'

const BLURBS: Record<string, string> = {
  'Green Hills': 'Jump, stomp, and read a crate row.',
  'Pipe Dreams': 'Vents are stairs — ride the spring chain.',
  'Cavern Depths': 'Climb the chimney. Wisps ignore rock.',
  'Spike Gauntlet': 'One hard tile is safe. Flux is the floor.',
  'Star Ride': 'Hold right across the sky catwalks.',
  'Brass Meadows': 'Gentle ridges, short gaps, twin vents.',
  'Copper Ridge': 'Pipe ladder and a staircase prize.',
  'Ember Trail': 'Springs, note-blocks, short flux bridges.',
  'Night Shift': 'First night watch — wisps and coin lines.',
  'Lantern Lane': 'Vent steps, a shuttle pit, a prism.',
  Gearworks: 'Saws, shelves, and a catwalk chimney.',
  'Flux Reservoir': 'Hop the flux stones past shardlings.',
  'Rivet Alley': 'Crawlers everywhere — clear a shell path.',
  'Scout Circuit': 'Wheel scouts and a lift to the terrace.',
  'Prism Bridge': 'Sky platforms with a mid-course prism.',
  'Cloud Pier': 'Spring tiers up to the high pier.',
  Iceworks: 'Slippery ice — trust the hard steps.',
  'Rotor Run': 'Vertical and horizontal rotors.',
  'Vault Ascent': 'Climb the vault to the roof flag.',
  'Workshop Finale': 'Everything the workshop taught you.',
}

const DIFFICULTY: Record<string, CourseDifficulty> = {
  'Green Hills': 'EASY',
  'Brass Meadows': 'EASY',
  'Copper Ridge': 'EASY',
  'Pipe Dreams': 'MED',
  'Ember Trail': 'MED',
  'Night Shift': 'MED',
  'Lantern Lane': 'MED',
  'Cavern Depths': 'MED',
  Gearworks: 'MED',
  'Flux Reservoir': 'MED',
  'Rivet Alley': 'MED',
  'Scout Circuit': 'MED',
  'Prism Bridge': 'MED',
  'Cloud Pier': 'MED',
  'Vault Ascent': 'MED',
  'Star Ride': 'HARD',
  Iceworks: 'HARD',
  'Rotor Run': 'HARD',
  'Spike Gauntlet': 'HARD',
  'Workshop Finale': 'HARD',
}

const THEME_COLOR: Record<string, string> = {
  overworld: '#769ca6',
  sky: '#a3c3c2',
  underground: '#101018',
  night: '#182050',
  castle: '#2a2438',
}

/** One-line pitch for the select preview strip. */
export function courseBlurb(name: string): string {
  return BLURBS[name] ?? 'A course from your blueprint shelf.'
}

/** Difficulty chip for the selector. */
export function courseDifficulty(
  name: string,
  builtIn: boolean,
): CourseDifficulty {
  if (!builtIn) return 'CUSTOM'
  return DIFFICULTY[name] ?? 'MED'
}

/** Theme swatch colour for list icons and the preview panel. */
export function themeColor(theme: string): string {
  return THEME_COLOR[theme] ?? THEME_COLOR.overworld!
}

/**
 * Index of the first user course (World 2), or `list.length` when every entry
 * is built-in.
 */
export function chapterBounds(list: { builtIn: boolean }[]): {
  workshopEnd: number
} {
  const at = list.findIndex((c) => !c.builtIn)
  return { workshopEnd: at < 0 ? list.length : at }
}

/** First visible row so `selected` stays inside a window of `visible` rows. */
export function scrollWindow(
  selected: number,
  length: number,
  visible: number,
): { first: number } {
  if (length <= 0 || visible <= 0) return { first: 0 }
  const vis = Math.min(visible, length)
  const first = Math.max(
    0,
    Math.min(selected - Math.floor(vis / 2), Math.max(0, length - vis)),
  )
  return { first }
}
